if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        this.scrollToProductDescription();
      }

      scrollToProductDescription() {
        const links = this.querySelectorAll('.js-product-subtitle-link');
        if (!links.length) return;

        const getAnchorScrollOffset = () => {
          let px = 20;
          const rootStyles = getComputedStyle(document.documentElement);
          const headerH = parseFloat(rootStyles.getPropertyValue('--header-height'));
          if (!Number.isNaN(headerH) && headerH > 0) px += headerH;
          const tabs = document.querySelector('.product-tabs-nav-wrapper');
          if (tabs && tabs.offsetHeight > 0) px += tabs.offsetHeight;
          return px;
        };

        const scrollToWithOffset = (el) => {
          if (!el) return;
          const offsetTop = getAnchorScrollOffset();
          const top = el.getBoundingClientRect().top + window.scrollY - offsetTop;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        };

        links.forEach((link) => {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            const href = (link.getAttribute('href') || '').trim();
            if (href.startsWith('#') && href.length > 1) {
              const id = href.slice(1);
              const target = document.getElementById(id) || document.querySelector(href);
              scrollToWithOffset(target);
              return;
            }
            scrollToWithOffset(document.querySelector('.product-description'));
          });
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        const localVariant = this.getVariantFromCurrentSelection();
        if (localVariant?.id) {
          params.push(`variant=${localVariant.id}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getVariantFromCurrentSelection() {
        const variantsScript = this.querySelector('variant-selects [data-variants]');
        if (!variantsScript) return null;

        let variants;
        try {
          variants = JSON.parse(variantsScript.textContent);
        } catch (e) {
          return null;
        }

        const selectedOptions = [];
        this.querySelectorAll('variant-selects .product-form__input').forEach((wrapper) => {
          const checked = wrapper.querySelector('input[type="radio"]:checked');
          if (checked) {
            selectedOptions.push(checked.value);
            return;
          }
          const select = wrapper.querySelector('select');
          if (select?.selectedOptions?.[0]) {
            selectedOptions.push(select.selectedOptions[0].value);
          }
        });

        if (!selectedOptions.length) return null;

        return variants.find((variant) => {
          const variantOptions = [variant.option1, variant.option2, variant.option3].filter(
            (opt) => opt !== null && opt !== undefined && opt !== '',
          );
          if (variantOptions.length !== selectedOptions.length) return false;
          return variantOptions.every((opt, index) => opt === selectedOptions[index]);
        });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variantFromHtml = this.getSelectedVariant(html);
          const variant = this.getVariantFromCurrentSelection() || variantFromHtml;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateColorVariantValue(html);

          if (!variant) {
            this.setUnavailable();
            this.showAvailableVariantLink();
            return;
          }

          const unavailableInfoElement = this.querySelector('.unavailable-info');
          if (unavailableInfoElement) {
            unavailableInfoElement.classList.add('hidden');
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.sectionId}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.className = source.className;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          applyVariantToPriceDisplay(variant, this.sectionId);

          if (typeof window.__drivetoSyncOptionalServicesPrice === 'function') {
            window.__drivetoSyncOptionalServicesPrice(variant);
          }

          const variantUpdated = new CustomEvent('variant:updated', {
            detail: {
              variant: variant,
            },
          });

          document.dispatchEvent(variantUpdated);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      showAvailableVariantLink() {
        if (!this.productVariants) {
          const variantsDataElement = this.querySelector('[data-variants]');
          if (variantsDataElement) {
            this.productVariants = JSON.parse(variantsDataElement.textContent);
          } else {
            return;
          }
        }

        const unavailableInfoElement = this.querySelector('.unavailable-info');
        if (!unavailableInfoElement) return;

        const unavailableLink = unavailableInfoElement.querySelector('.unavailable-info__link');

        if (!unavailableLink) return;

        const productUrl = this.dataset.url;

        const fieldsets = Array.from(this.querySelectorAll('.product-form__input'));
        const selectedOptions = fieldsets
          .map((fieldset) => {
            const input = fieldset.querySelector('input:checked, select');
            return input ? input.value : null;
          })
          .filter(Boolean);

        let bestMatch = null;
        let bestMatchCount = -1;

        this.productVariants.forEach((v) => {
          if (v.available) {
            let currentMatchCount = 0;
            v.options.forEach((optionValue, index) => {
              if (selectedOptions[index] && optionValue === selectedOptions[index]) {
                currentMatchCount++;
              }
            });

            if (currentMatchCount > bestMatchCount) {
              bestMatchCount = currentMatchCount;
              bestMatch = v;
            }
          }
        });

        if (bestMatch) {
          unavailableLink.href = `${productUrl}?variant=${bestMatch.id}`;
          unavailableLink.classList.remove('hidden');
        } else {
          unavailableLink.classList.add('hidden');
        }
      }

      updateColorVariantValue(html) {
        const selectedColorOptionEl = html.querySelector('.variant-color-selected');
        const selectedColorOptionElPrev = document.querySelector('.variant-color-selected');

        if (!selectedColorOptionEl) return;

        const selectedColorOption = html.querySelector('.variant-color input:checked');
        const value = selectedColorOption?.getAttribute('value');
        selectedColorOptionEl.textContent = value;
        HTMLUpdateUtility.viewTransition(
          selectedColorOptionElPrev,
          selectedColorOptionEl,
          this.preProcessHtmlCallbacks,
        );
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.sectionId}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
        document.querySelector('.unavailable-info').classList.remove('hidden');
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch(() => {})
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}

function formatLeaseMoney(amount) {
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function fillLegalPriceTemplate(template, amount) {
  const money = formatLeaseMoney(amount);
  return String(template || '')
    .replace(/\[\s*price\s*\]/gi, money)
    .replace(/\{\{\s*price\s*\}\}/gi, money)
    .replace(/\{\s*price\s*\}/gi, money);
}

function setLeasePriceLineItem(itemEl, amount, fallbackSuffix) {
  if (!itemEl) return;
  const originalText = itemEl.textContent.trim();
  const priceTextMatch = originalText.match(/(bez DPH|s DPH)/i);
  const priceText = priceTextMatch ? priceTextMatch[0] : fallbackSuffix || '';
  itemEl.textContent = formatLeaseMoney(amount) + (priceText ? ` ${priceText}` : '');
}

function setDiscountCardPrimary(primaryEl, amount) {
  if (!primaryEl) return;
  const suffix = primaryEl.querySelector('.price-discount-card__primary-suffix');
  const sr = primaryEl.querySelector('.visually-hidden');
  primaryEl.textContent = '';
  if (sr) primaryEl.appendChild(sr);
  primaryEl.appendChild(document.createTextNode(formatLeaseMoney(amount)));
  if (suffix) primaryEl.appendChild(suffix);
}

function setMetaSecondaryText(metaEl, amount, suffixText) {
  if (!metaEl) return;
  const secondary = metaEl.querySelector('.price-discount-card__meta-secondary');
  if (!secondary) return;
  secondary.textContent = suffixText ? `${formatLeaseMoney(amount)} ${suffixText}` : formatLeaseMoney(amount);
}

function applyVariantToPriceDisplay(variant, sectionId) {
  if (!variant || !sectionId) return;

  const priceRoot = document.querySelector(`product-info #price-${sectionId}`);
  if (!priceRoot) return;

  const container = priceRoot.querySelector('.price__container');
  if (!container) return;

  const price = variant.price / 100;
  const compareAt = (variant.compare_at_price || 0) / 100;
  const priceVat = Math.round(price * 1.21);
  const compareVat = Math.round(compareAt * 1.21);
  const savings = compareAt > price ? compareAt - price : 0;
  const savingsVat = Math.round(savings * 1.21);

  const discountPrimaryWithoutVat = container.querySelector('.price-discount-card__primary.withoutVat');
  const discountPrimaryWithVat = container.querySelector('.price-discount-card__primary.withVat');

  if (discountPrimaryWithoutVat || discountPrimaryWithVat) {
    setDiscountCardPrimary(discountPrimaryWithoutVat, price);
    setDiscountCardPrimary(discountPrimaryWithVat, priceVat);

    const promoWithoutVat = container.querySelector('.price-discount-card__promo-amount.withoutVat');
    const promoWithVat = container.querySelector('.price-discount-card__promo-amount.withVat');
    if (promoWithoutVat) promoWithoutVat.textContent = savings > 0 ? formatLeaseMoney(savings) : formatLeaseMoney(0);
    if (promoWithVat) promoWithVat.textContent = savingsVat > 0 ? formatLeaseMoney(savingsVat) : formatLeaseMoney(0);

    container
      .querySelectorAll('.price-discount-card__meta.withoutVat .price-discount-card__meta-compare')
      .forEach((el) => {
        if (compareAt > 0) el.textContent = formatLeaseMoney(compareAt);
      });
    container
      .querySelectorAll('.price-discount-card__meta.withVat .price-discount-card__meta-compare')
      .forEach((el) => {
        if (compareVat > 0) el.textContent = formatLeaseMoney(compareVat);
      });

    const metaWithoutVat = container.querySelector('.price-discount-card__meta.withoutVat');
    const metaWithVat = container.querySelector('.price-discount-card__meta.withVat');
    const withoutSuffix =
      metaWithoutVat
        ?.querySelector('.price-discount-card__meta-secondary')
        ?.textContent.match(/(bez DPH|s DPH)/i)?.[0] || 's DPH';
    const withSuffix =
      metaWithVat?.querySelector('.price-discount-card__meta-secondary')?.textContent.match(/(bez DPH|s DPH)/i)?.[0] ||
      'bez DPH';
    setMetaSecondaryText(metaWithoutVat, priceVat, withoutSuffix);
    setMetaSecondaryText(metaWithVat, price, withSuffix);

    const tooltip = container.querySelector('.price-discount-legal-info__tooltip');
    const trigger = container.querySelector('.price-discount-legal-info__trigger');
    if (tooltip && compareAt > 0) {
      const template = tooltip.getAttribute('data-legal-template') || tooltip.textContent || '';
      const legalText = fillLegalPriceTemplate(template, compareAt);
      tooltip.textContent = legalText;
      if (trigger) trigger.setAttribute('aria-label', legalText);
    }
    return;
  }

  setLeasePriceLineItem(container.querySelector('.price__regular.withoutVat .price-item--regular'), price, 'bez DPH');
  setLeasePriceLineItem(container.querySelector('.price__regular.withVat .price-item--regular'), priceVat, 's DPH');
  setLeasePriceLineItem(container.querySelector('.price__sale.withoutVat .price-item--sale'), price, 'bez DPH');
  setLeasePriceLineItem(container.querySelector('.price__sale.withVat .price-item--sale'), priceVat, 's DPH');
}

function updateBasePriceInputs(variant) {
  if (!variant) return;

  let basePriceInput = document.getElementById('product-base-price');
  if (!basePriceInput) {
    basePriceInput = document.createElement('input');
    basePriceInput.type = 'hidden';
    basePriceInput.id = 'product-base-price';
    document.body.appendChild(basePriceInput);
  }
  basePriceInput.value = variant.price;

  let basePriceWithVatInput = document.getElementById('product-base-price-with-vat');
  if (!basePriceWithVatInput) {
    basePriceWithVatInput = document.createElement('input');
    basePriceWithVatInput.type = 'hidden';
    basePriceWithVatInput.id = 'product-base-price-with-vat';
    document.body.appendChild(basePriceWithVatInput);
  }
  basePriceWithVatInput.value = Math.round(variant.price * 1.21);
}

document.addEventListener('variant:updated', function (e) {
  const variant = e.detail.variant;
  if (!variant) return;

  let infoDiv = null;
  if (e.target && typeof e.target.closest === 'function') {
    const productInfoElement = e.target.closest('product-info');
    if (productInfoElement) {
      infoDiv = productInfoElement.querySelector('.product-information');
    }
  }
  if (!infoDiv) {
    infoDiv = document.querySelector('product-info .product-information');
  }

  if (!infoDiv) return;

  infoDiv.dataset.variantId = variant.id;
  infoDiv.dataset.productPrice = formatMoney(variant.price);
  infoDiv.dataset.productPriceWithVat = formatMoney(Math.round(variant.price * 1.21));
  const compareAt = Number(variant.compare_at_price) || 0;
  if (compareAt > variant.price && compareAt > 0) {
    infoDiv.dataset.productCompareAtPrice = formatMoney(compareAt);
    infoDiv.dataset.productCompareAtPriceWithVat = formatMoney(Math.round(compareAt * 1.21));
  } else {
    infoDiv.dataset.productCompareAtPrice = '';
    infoDiv.dataset.productCompareAtPriceWithVat = '';
  }

  updateBasePriceInputs(variant);

  infoDiv.dataset.productUrl = `${window.location.pathname}?variant=${variant.id}`;
  infoDiv.dataset.productColor = variant.option3 || '';
  infoDiv.dataset.productRentalPeriod = variant.option2 || '';
  infoDiv.dataset.productAnnualMileage = variant.option1 || '';
  infoDiv.dataset.productImage =
    variant.featured_image && variant.featured_image.src ? variant.featured_image.src : infoDiv.dataset.productImage;
});

document.addEventListener('DOMContentLoaded', function () {
  const variantIdInput = document.querySelector('variant-selects [name="id"], form[action*="/cart/add"] [name="id"]');
  if (variantIdInput) {
    const variantId = variantIdInput.value || variantIdInput.textContent;
    if (variantId) {
      const variantsData = document.querySelector('variant-selects [data-variants]');
      if (variantsData) {
        try {
          const variants = JSON.parse(variantsData.textContent);
          const variant = variants.find((v) => String(v.id) === String(variantId));
          if (variant) {
            updateBasePriceInputs(variant);
          }
        } catch (e) {}
      }
    }
  }
});
